import React from 'react';
import TablerIconUser from '~icons/tabler/user';

import { cn } from '#/utils/classnames';

export type AvatarSize = 'small' | 'medium' | 'large';

export type AvatarVariant = 'circular' | 'rounded' | 'square';

export interface AvatarOwnProps {
  alt?: string;
  children?: React.ReactNode;
  className?: string;
  sizes?: string;
  src?: string;
  srcSet?: string;
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
  variant?: AvatarVariant;
  size?: AvatarSize;
}

export type AvatarProps<C extends AvatarComponent = AvatarDefaultComponent> =
  AvatarPropsWithComponent<C>;

function Avatar<C extends AvatarComponent = AvatarDefaultComponent>({
  alt,
  children,
  className = '',
  component,
  imgProps = {},
  sizes = '',
  srcSet,
  src,
  variant = 'circular',
  size = 'medium',
  ...props
}: AvatarProps<C>) {
  const Component = component || 'div';
  const [failed, setFailed] = React.useState(false);
  return (
    <Component
      className={cn(
        'gooey-avatar',
        'd-avatar bg-gray-700 text-gray-300 flex items-center justify-center overflow-hidden',
        sizeClassnamesMap[size],
        variantClassnamesMap[variant],
        className,
        ((!src && !children && !srcSet) || failed) && 'd-avatar-placeholder'
      )}
      {...props}
    >
      {children}
      {src || srcSet ? (
        <img
          {...imgProps}
          loading={imgProps.loading ?? 'lazy'}
          alt={alt ?? 'Avatar'}
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          className='h-full w-full object-cover'
          onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            if (imgProps.onError) {
              imgProps.onError(e);
            }
            setFailed(true);
          }}
        />
      ) : (
        !children && <TablerIconUser className='h-3/5 w-3/5' />
      )}
    </Component>
  );
}
Avatar.displayName = 'GooeyAvatar';

export default Avatar;

/*
|------------------
| Internals
|------------------
*/

// Size mapping similar to MUI defaults
const sizeClassnamesMap: SizeMap = {
  small: 'h-8 w-8 text-sm',
  medium: 'h-10 w-10 text-base',
  large: 'h-12 w-12 text-lg',
};

// Variant styles
const variantClassnamesMap: VariantMap = {
  circular: 'rounded-full',
  rounded: 'rounded-lg',
  square: 'rounded-none',
};

type AvatarComponent = React.ElementType;

// Intersection type for the component prop override
type AvatarPropsWithComponent<C extends AvatarComponent> = AvatarOwnProps & {
  component?: C;
} & Omit<React.ComponentPropsWithoutRef<C>, keyof AvatarOwnProps>;

// Default type when no component prop is provided
type AvatarDefaultComponent = 'div';

// Size mapping type
type SizeMap = {
  [key in AvatarSize]: string;
};

// Variant mapping type
type VariantMap = {
  [key in AvatarVariant]: string;
};
